import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Trophy } from 'lucide-react'
import type { AttendantRanking } from '@/types/attendance-metrics'
import { cn } from '@/lib/utils'

interface CompactRankingTableProps {
  rankings: AttendantRanking | null
  isLoading?: boolean
  limit?: number
}

const getMedalEmoji = (position: number) => {
  switch (position) {
    case 1:
      return '🏆'
    case 2:
      return '🥈'
    case 3:
      return '🥉'
    default:
      return null
  }
}

export function CompactRankingTable({ rankings, isLoading, limit = 10 }: CompactRankingTableProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rankings</CardTitle>
          <CardDescription>Top atendentes por métrica</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!rankings || !rankings.byResponses || rankings.byResponses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rankings</CardTitle>
          <CardDescription>Top atendentes por métrica</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Trophy className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              Sem dados para ranking no período selecionado
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Merge rankings by attendant (using responses as base, join with messages and tickets)
  const mergedRankings = rankings.byResponses.slice(0, limit).map((respItem, index) => {
    const msgItem = rankings.byMessages.find(
      (m) => m.attendantOdataId === respItem.attendantOdataId
    )
    const ticketItem = rankings.byTickets.find(
      (t) => t.attendantOdataId === respItem.attendantOdataId
    )

    return {
      position: index + 1,
      attendantOdataId: respItem.attendantOdataId,
      attendantName: respItem.attendantName,
      responses: respItem.value,
      messages: msgItem?.value || 0,
      tickets: ticketItem?.value || 0,
    }
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rankings</CardTitle>
        <CardDescription>Top {limit} atendentes por métrica</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Desktop: Compact Table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 text-center">#</TableHead>
                <TableHead>Atendente</TableHead>
                <TableHead className="text-right">Respostas</TableHead>
                <TableHead className="text-right">Mensagens</TableHead>
                <TableHead className="text-right">Atendimentos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mergedRankings.map((attendant) => {
                const medal = getMedalEmoji(attendant.position)
                const isTopThree = attendant.position <= 3

                return (
                  <TableRow
                    key={attendant.attendantOdataId}
                    className={cn(
                      'transition-colors',
                      isTopThree && 'bg-muted/50 hover:bg-muted/70'
                    )}
                  >
                    <TableCell className="text-center font-medium">
                      {medal || attendant.position}
                    </TableCell>
                    <TableCell className="font-medium">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default">{attendant.attendantName}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">ID: {attendant.attendantOdataId}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-primary">
                      {attendant.responses}
                    </TableCell>
                    <TableCell className="text-right">{attendant.messages}</TableCell>
                    <TableCell className="text-right">{attendant.tickets}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>

        {/* Mobile: Horizontal Scroll Table */}
        <div className="md:hidden overflow-x-auto -mx-6 px-6">
          <Table className="min-w-[600px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 text-center sticky left-0 bg-card z-10">#</TableHead>
                <TableHead className="sticky left-12 bg-card z-10 min-w-[150px]">
                  Atendente
                </TableHead>
                <TableHead className="text-right">Resp.</TableHead>
                <TableHead className="text-right">Msgs</TableHead>
                <TableHead className="text-right">Atend.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mergedRankings.map((attendant) => {
                const medal = getMedalEmoji(attendant.position)
                const isTopThree = attendant.position <= 3

                return (
                  <TableRow
                    key={attendant.attendantOdataId}
                    className={cn(isTopThree && 'bg-muted/50')}
                  >
                    <TableCell className="text-center font-medium sticky left-0 bg-card">
                      {medal || attendant.position}
                    </TableCell>
                    <TableCell className="font-medium sticky left-12 bg-card text-sm">
                      {attendant.attendantName}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-primary text-sm">
                      {attendant.responses}
                    </TableCell>
                    <TableCell className="text-right text-sm">{attendant.messages}</TableCell>
                    <TableCell className="text-right text-sm">{attendant.tickets}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
